// frontend/src/components/dashboard/PriorityPanel.js
//
// Everything outstanding, once.
//
// The page used to carry three of these. "What needs doing" near the top,
// "Active Alerts" a screen further down, and a "Reconciliation Status" panel
// below that with its own sentence of guidance — and they overlapped. The same
// unfinished reconciliation appeared in all three, the same 45 open exceptions
// were printed twice with the same £1,663 beside them, and a manager had to
// read the whole page to be sure they had seen everything.
//
// One list, ordered by what it costs to ignore. Work that stops the day being
// filed comes first, then money at risk, then things that will bite later in
// the week. Each line says what it is, what it is worth, and has exactly one
// button.

import React from 'react';

import { Box, Button, Chip, Stack, Typography } from '@mui/material';

import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';

const TONES = {
  /* Stops the day being filed, or money already gone. */
  urgent: { bar: '#b3261e', ink: '#8c1d18', chip: '#fdecea', label: 'Now' },

  /* Will stop something soon, or needs a decision this week. */
  soon: { bar: '#b45309', ink: '#8a5200', chip: '#fff4df', label: 'This week' },

  /* Worth knowing, nothing blocked. */
  note: { bar: '#5d6f66', ink: '#41514a', chip: '#eef2f0', label: 'Note' },
};

const ORDER = { urgent: 0, soon: 1, note: 2 };

/**
 * One thing to do.
 *
 * `value` is the money or the count at stake, printed on the right where the
 * eye can run down it — a list of jobs with no sizes on it gives a manager no
 * way to choose between them.
 */
function PriorityRow({ item, last }) {
  const tone = TONES[item.tone] || TONES.note;

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: last ? 'none' : '1px solid',
        borderColor: 'divider',
        boxShadow: `inset 3px 0 0 ${tone.bar}`,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontWeight: 800, fontSize: 14.5, color: tone.ink }}>
            {item.title}
          </Typography>

          <Chip
            size="small"
            label={tone.label}
            sx={{
              height: 19,
              fontSize: 10.5,
              fontWeight: 800,
              background: tone.chip,
              color: tone.ink,
            }}
          />
        </Stack>

        {item.detail ? (
          <Typography variant="body2" color="text.secondary" sx={{ pt: 0.25 }}>
            {item.detail}
          </Typography>
        ) : null}
      </Box>

      {item.value ? (
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: 16,
            color: tone.ink,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {item.value}
        </Typography>
      ) : null}

      {item.cta ? (
        <Button
          size="small"
          variant={item.tone === 'urgent' ? 'contained' : 'outlined'}
          onClick={item.onClick}
          sx={{
            fontWeight: 800,
            minWidth: 104,
            flexShrink: 0,
            ...(item.tone === 'urgent'
              ? { background: '#b3261e', '&:hover': { background: '#8c1d18' } }
              : {}),
          }}
        >
          {item.cta}
        </Button>
      ) : null}
    </Stack>
  );
}

export default function PriorityPanel({ items = [], dueLabel, loading = false, action }) {
  const ordered = [...items].sort(
    (a, b) => (ORDER[a.tone] ?? 9) - (ORDER[b.tone] ?? 9)
  );

  const urgent = ordered.filter((item) => item.tone === 'urgent').length;

  if (loading) return null;

  /*
   * A clear day says so in one line.
   *
   * An empty heading reads as a section that failed to load, and a manager who
   * has learnt to skip it on the quiet days will skip it on the day it matters.
   */
  if (!ordered.length) {
    return (
      <Box
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: '#b7ddc6',
          background: '#f2f9f5',
          px: 2.5,
          py: 2,
          display: 'flex',
          gap: 1.75,
          alignItems: 'center',
        }}
      >
        <CheckCircleRounded sx={{ color: '#0f7a43', fontSize: 30 }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, color: '#08301c' }}>
            {dueLabel} is filed and nothing is outstanding
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No unfinished work, no variances waiting for sign-off, no stock or paperwork due.
          </Typography>
        </Box>

        {/* The clear day is the one a manager still needs the directory on. */}
        {action}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: '#f6faf7',
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="baseline">
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 900,
              letterSpacing: 0.7,
              textTransform: 'uppercase',
              color: '#034f2d',
            }}
          >
            Needs doing
          </Typography>

          <Typography variant="caption" color="text.secondary">
            {ordered.length} {ordered.length === 1 ? 'item' : 'items'}
            {urgent ? ` · ${urgent} blocking` : ''}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            for {dueLabel}
          </Typography>

          {action}
        </Stack>
      </Stack>

      {ordered.map((item, index) => (
        <PriorityRow key={item.key} item={item} last={index === ordered.length - 1} />
      ))}
    </Box>
  );
}
